import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue'
import { useI18n } from 'vue-i18n'
import useGet from '@/core/composables/api/useGet'
import usePost from '@/core/composables/api/usePost'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { useSnackStore } from '@/stores/snack.store'

type MemberAccessCode = {
  memberId: string
  firstName: string
  lastName: string
  accessCode: string | null
}

export const useMemberAccessCode = (memberId: MaybeRefOrGetter<string | undefined>) => {
  const { t } = useI18n()
  const snackStore = useSnackStore()

  const endpoint = computed(() => `portal/members/${toValue(memberId)}/access-code`)
  const { data, status, load } = useGet<MemberAccessCode>(endpoint)
  const {
    data: regenerated,
    status: regenerateStatus,
    mutate,
  } = usePost<{ accessCode: string }>(endpoint)

  const accessCodeOverride = ref<string>()

  const accessCode = computed(() => {
    if (accessCodeOverride.value !== undefined) {
      return accessCodeOverride.value
    }
    return data.value?.accessCode ?? null
  })

  const loadAccessCode = async () => {
    if (!toValue(memberId)) {
      return
    }
    accessCodeOverride.value = undefined
    await load()
  }

  const regenerateAccessCode = async () => {
    if (regenerateStatus.value === ApiRequestStatus.Loading) {
      return
    }
    const replacing = accessCode.value != null
    await mutate()
    if (regenerateStatus.value === ApiRequestStatus.Success && regenerated.value?.accessCode) {
      accessCodeOverride.value = regenerated.value.accessCode
      snackStore.showInfo(
        t(
          replacing
            ? 'organization.loginLessMember.accessCode.regenerateSuccess'
            : 'portal.accessCode.generateSuccess',
        ),
      )
      return
    }
    snackStore.showError(t('organization.loginLessMember.accessCode.regenerateError'))
  }

  return {
    accessCode,
    status,
    regenerateStatus,
    loadAccessCode,
    regenerateAccessCode,
  }
}
